import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useToolset } from "@/hooks/useToolset";
import { cn } from "@/utils";
import { ToolSetTrigger } from "@cohesive/api-client";
import { capitalCase } from "change-case";
import {
  Bot,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Search,
  Zap,
} from "lucide-react";
import React, { useMemo, useState } from "react";

interface IntegrationTriggersProps {
  toolSetID: string;
  integrationName: string;
}

const parseDescription = (description: string) => {
  // Extract documentation links and clean description text
  const documentationRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g;

  let cleanDescription = description;
  const documentationLinks: Array<{ text: string; url: string }> = [];

  let match;
  while ((match = documentationRegex.exec(description)) !== null) {
    const [fullMatch, linkText, linkUrl] = match;
    documentationLinks.push({ text: linkText, url: linkUrl });
    // Remove the markdown link from the description
    cleanDescription = cleanDescription.replace(fullMatch, "").trim();
  }

  // Clean up any double spaces or trailing punctuation
  cleanDescription = cleanDescription
    .replace(/\s+/g, " ")
    .replace(/\s+([.!?])/g, "$1")
    .trim();

  return { cleanDescription, documentationLinks };
};

const TriggerCard: React.FC<{
  trigger: ToolSetTrigger;
  onClick?: () => void;
}> = ({ trigger, onClick }) => {
  const { cleanDescription, documentationLinks } = parseDescription(
    trigger.description || "No description available",
  );

  return (
    <div
      className={cn(
        "group relative border rounded-lg p-4 transition-all duration-200 bg-gradient-to-br from-background to-muted/20 flex flex-col min-h-[140px]",
        {
          "hover:shadow-md hover:border-primary/20": onClick,
        },
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-rust/10 text-orange-600">
            <Zap className="h-4 w-4" />
          </div>
          <div className="flex items-center gap-2">
            <h4 className="font-medium text-sm">{trigger.name}</h4>
            <CheckCircle2 className="h-4 w-4 text-lc-active" />
          </div>
        </div>
      </div>

      <div className="text-sm text-muted-foreground leading-relaxed flex-1 mb-3 line-clamp-10">
        {cleanDescription}
      </div>

      {/* Documentation Links - Always Anchored to Bottom */}
      <div className="mt-auto">
        {documentationLinks.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-2">
            {documentationLinks.map((link, index) => {
              // Standardize common documentation link texts
              const standardizedText =
                link.text.toLowerCase().includes("doc") ||
                link.text.toLowerCase().includes("learn") ||
                link.text.toLowerCase().includes("guide") ||
                link.text.toLowerCase().includes("reference")
                  ? "View Documentation"
                  : link.text;

              return (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary/90 text-xs font-medium transition-all duration-200 border border-primary/20 hover:border-primary/30"
                >
                  <ExternalLink className="h-3 w-3" />
                  {capitalCase(standardizedText)}
                </a>
              );
            })}
          </div>
        )}
      </div>

      {/* Subtle animation on hover */}
      <div
        className={cn(
          "absolute inset-0 rounded-lg bg-gradient-to-r from-rust/5 to-yellow-500/5 opacity-0 transition-opacity duration-200 pointer-events-none",
          {
            "group-hover:opacity-100": onClick,
          },
        )}
      />
    </div>
  );
};

const LoadingSkeleton: React.FC = () => {
  return (
    <div className="space-y-4">
      <Skeleton className="h-10 w-full max-w-md" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Array.from({ length: 6 }).map((_, index) => (
          <div key={index} className="border rounded-lg p-4">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <Skeleton className="h-7 w-7 rounded-md" />
                <Skeleton className="h-4 w-24" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
    </div>
  );
};

const EmptyState: React.FC<{
  integrationName: string;
  searchQuery: string;
}> = ({ integrationName, searchQuery }) => {
  return (
    <div className="text-center py-12">
      <div className="mx-auto w-12 h-12 bg-muted rounded-full flex items-center justify-center mb-4">
        <Bot className="h-6 w-6 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-medium mb-2">
        {searchQuery ? "No triggers found" : "No triggers available"}
      </h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        {searchQuery ? (
          <>
            No triggers match your search for "{searchQuery}". Try a different
            search term.
          </>
        ) : (
          <>
            {integrationName} doesn't have any configured triggers yet. Check
            back later as more features become available.
          </>
        )}
      </p>
    </div>
  );
};

export const IntegrationTriggers: React.FC<IntegrationTriggersProps> = ({
  toolSetID,
  integrationName,
}) => {
  const { data: toolSet, isLoading, error } = useToolset(toolSetID);
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  const enabledCount = toolSet?.triggers?.length || 0;
  const totalCount = toolSet?.triggers?.length || 0;

  // Filter tool specs based on search query
  const filteredTriggers = useMemo(() => {
    if (!searchQuery.trim()) return toolSet?.triggers || [];

    const query = searchQuery.toLowerCase();
    return (
      toolSet?.triggers?.filter(
        (trigger) =>
          trigger.name.toLowerCase().includes(query) ||
          trigger.description.toLowerCase().includes(query) ||
          trigger.key.toLowerCase().includes(query),
      ) || []
    );
  }, [toolSet?.triggers, searchQuery]);

  // Determine how many items to show
  const itemsPerRow = 2; // md:grid-cols-2
  const maxRowsDefault = 3;
  const maxItemsDefault = maxRowsDefault * itemsPerRow;

  const visibleTriggers = isExpanded
    ? filteredTriggers
    : filteredTriggers?.slice(0, maxItemsDefault);

  const hasMoreItems = filteredTriggers?.length > maxItemsDefault;

  if (error) {
    return null; // Gracefully handle errors by not showing the section
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Zap className="h-5 w-5 text-orange-600" />
          <CardTitle>Available Triggers</CardTitle>
        </div>
        <CardDescription>
          {isLoading ? (
            "Loading available triggers..."
          ) : totalCount > 0 ? (
            <>
              Discover what events {integrationName} can trigger.
              {enabledCount > 0 && (
                <span className="text-primary font-medium">
                  {" "}
                  {enabledCount} of {totalCount} triggers are enabled.
                </span>
              )}
            </>
          ) : (
            `See what events ${integrationName} can trigger for you.`
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <LoadingSkeleton />
        ) : totalCount === 0 ? (
          <EmptyState integrationName={integrationName} searchQuery="" />
        ) : (
          <div className="space-y-4">
            {/* Search Input */}
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search triggers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Triggers Grid */}
            {filteredTriggers.length === 0 ? (
              <EmptyState
                integrationName={integrationName}
                searchQuery={searchQuery}
              />
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {visibleTriggers.map((trigger) => (
                    <TriggerCard key={trigger.id} trigger={trigger} />
                  ))}
                </div>

                {/* Expand/Collapse Button */}
                {hasMoreItems && !searchQuery && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="flex items-center gap-2"
                    >
                      {isExpanded ? (
                        <>
                          Show Less
                          <ChevronUp className="h-4 w-4" />
                        </>
                      ) : (
                        <>
                          Show {filteredTriggers.length - maxItemsDefault} More
                          <ChevronDown className="h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
